import React, { useState, useEffect } from 'react';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import { FaArrowRight, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './InvesterAccountPage.scss';

export default function InvesterAccountPage() {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate                  = useNavigate();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: all } = await api.get('/Company');
        const owners = all.data.filter(c => c.hasTowerCrane);

        const reports = await Promise.all(
          owners.map(c =>
            api
              .get(`/Rental/Report/${c.id}`)
              .then(r => r.data.data)
              .catch(() => ({
                totalProgressPayment: 0,
                totalNonConstructionPayment: 0,
                remainingPayment: 0,
                totalNonConstructionPaymentInvoiceAmount: 0
              }))
          )
        );

        const list = owners
          .map((c, i) => ({
            id: c.id,
            name: c.name,
            totalProgressPayment:          reports[i].totalProgressPayment,
            totalNonConstructionPayment:   reports[i].totalNonConstructionPayment,
            remainingPayment:              reports[i].totalProgressPayment-reports[i].totalNonConstructionPayment,
            totalNonConstructionPaymentInvoiceAmount:
              reports[i].totalNonConstructionPaymentInvoiceAmount
          }))
          // Filtre: Hakediş, Ödenen veya Kalan 0'ın üstünde
          .filter(r =>
            (r.totalProgressPayment   || 0) > 0 ||
            (r.totalNonConstructionPayment || 0) > 0 ||
            (r.remainingPayment       || 0) > 0
          );

        if (active) setRows(list);
      } catch (err) {
        console.error('Veri çekme hatası:', err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Grand totals
  const grandProgress   = rows.reduce((sum, r) => sum + (r.totalProgressPayment || 0), 0);
  const grandPaid       = rows.reduce((sum, r) => sum + (r.totalNonConstructionPayment || 0), 0);
  const grandRemaining  = rows.reduce((sum, r) => sum + (r.remainingPayment || 0), 0);
  const grandInvoiced   = rows.reduce((sum, r) => sum + (r.totalNonConstructionPaymentInvoiceAmount || 0), 0);
  const grandToBeBilled = rows.reduce(
    (sum, r) =>
      sum + ((r.totalProgressPayment || 0) - (r.totalNonConstructionPaymentInvoiceAmount || 0)),
    0
  );

  // Max for shading
  const maxRemaining = rows.length
    ? Math.max(...rows.map(r => r.remainingPayment || 0))
    : 1;

  // Sort by remainingPayment
  const sortedRows = [...rows].sort((a, b) =>
    sortOrder === 'desc'
      ? (b.remainingPayment || 0) - (a.remainingPayment || 0)
      : (a.remainingPayment || 0) - (b.remainingPayment || 0)
  );

  if (loading) {
    return (
      <div className="my-account-page">
        <Navbar />
        <div className="content"><p>Yükleniyor…</p></div>
      </div>
    );
  }

  return (
    <div className="my-account-page">
      <Navbar/>

      <div className="content">
        <header className="page-header">
          <h1>Yatırımcı Muhasebesi</h1>
        </header>

        {/* Grand Totals */}
        <div className="totals">
          <div className="total-card progress">
            <span className="label">Toplam Yatırımcı Hakedişi</span>
            <span className="value">{Number(grandProgress).toLocaleString()} TL</span>
          </div>
          <div className="total-card paid">
            <span className="label">Toplam Yapılan Ödeme</span>
            <span className="value">{Number(grandPaid).toLocaleString()} TL</span>
          </div>
          <div className="total-card remaining">
            <span className="label">Toplam Kalan</span>
            <span className="value">{Number(grandRemaining).toLocaleString()} TL</span>
          </div>
          <div className="total-card invoiced">
            <span className="label">Toplam Kesilen Fatura</span>
            <span className="value">{Number(grandInvoiced).toLocaleString()} TL</span>
          </div>
          <div className="total-card tobebilled">
            <span className="label">Toplam Kesilmesi Gereken Fatura</span>
            <span className="value">{Number(grandToBeBilled).toLocaleString()} TL</span>
          </div>
        </div>

        {/* Per-company table */}
        <div className="table-container">
          <table className="companies-table">
            <thead>
              <tr>
                <th>Firma Adı</th>
                <th>Hakediş</th>
                <th>Ödenen</th>
                <th
                  className="sortable"
                  onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                >
                  Kalan {sortOrder === 'desc' ? <FaSortAmountDown/> : <FaSortAmountUp/>}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(r => {
                const ratio     = maxRemaining > 0 ? (r.remainingPayment || 0) / maxRemaining : 0;
                const lightness = 100 - ratio * 50;
                const bgColor   = `hsl(0, 100%, ${lightness}%)`;

                return (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td>{Number(r.totalProgressPayment || 0).toLocaleString()} TL</td>
                    <td>{Number(r.totalNonConstructionPayment || 0).toLocaleString()} TL</td>
                    <td
                      className="remaining-cell"
                      style={{ backgroundColor: bgColor }}
                    >
                      {Number(r.remainingPayment || 0).toLocaleString()} TL
                    </td>
                    <td className="actions">
                      <button
                        className="go-btn"
                        onClick={() => navigate(`/accountpage/${r.id}`)}
                        aria-label="Detayları Gör"
                      >
                        <FaArrowRight/>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan="5">Hiç kule vinç sahibi firma bulunamadı.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
